import { useEffect, useState } from "react";
import { Award, Headphones, ShieldCheck, Star, TrendingUp, Zap } from "lucide-react";
import { getSocialProof, type SocialProof } from "@/api/client";

const GOOGLE_REVIEW_URL = "https://share.google/FPGrKt0hro0DPhh6r";

interface StaticItem {
  key: string;
  icon: typeof ShieldCheck;
  color: "primary" | "warning" | "success" | "secondary";
  value: string;
  label: string;
}

// Static items below are always-true qualitative statements, not inflated
// metrics. Quantitative numbers (reviews, certificates issued) come from the
// live /api/public/social-proof endpoint and only render when real.
const STATIC_ITEMS: StaticItem[] = [
  {
    key: "emission",
    icon: Zap,
    color: "warning",
    value: "~15 min",
    label: "Emissão pós-validação",
  },
  {
    key: "icp",
    icon: ShieldCheck,
    color: "success",
    value: "ICP-Brasil",
    label: "Validade jurídica plena",
  },
  {
    key: "support",
    icon: Headphones,
    color: "secondary",
    value: "Suporte humano",
    label: "Atendimento real",
  },
];

const colorBg: Record<string, string> = {
  primary: "bg-primary/10 text-primary",
  warning: "bg-warning/10 text-warning",
  success: "bg-success/10 text-success",
  secondary: "bg-secondary/10 text-secondary",
};

export const TrustBar = () => {
  const [proof, setProof] = useState<SocialProof | null>(null);

  useEffect(() => {
    void getSocialProof()
      .then(setProof)
      .catch(() => undefined);
  }, []);

  const items: StaticItem[] = [...STATIC_ITEMS];

  // Add dynamic "certificates emitted" only when we have real numbers.
  if (proof && proof.certificatesEmitted > 0) {
    items.unshift({
      key: "certs",
      icon: TrendingUp,
      color: "primary",
      value: proof.certificatesEmitted.toLocaleString("pt-BR"),
      label: proof.certificatesEmitted === 1 ? "Certificado emitido" : "Certificados emitidos",
    });
  }

  return (
    <section className="bg-gradient-to-r from-primary/5 via-secondary/5 to-primary/5 border-y border-border py-6 md:py-8">
      <div className="container">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <div key={it.key} className="flex items-center gap-3">
                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${colorBg[it.color]}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-base md:text-lg font-extrabold text-foreground leading-none truncate">
                    {it.value}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-1 leading-tight">
                    {it.label}
                  </p>
                </div>
              </div>
            );
          })}

          {/* Google rating — render only when real data is available */}
          {proof?.googleReviews && proof.googleReviews.totalRatings > 0 && (
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 group"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-warning/10 text-warning">
                <Star className="h-6 w-6 fill-warning" />
              </div>
              <div className="min-w-0">
                <div className="flex items-baseline gap-1">
                  <p className="text-base md:text-lg font-extrabold text-foreground tabular-nums leading-none">
                    {proof.googleReviews.rating.toFixed(1)}
                  </p>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star
                        key={i}
                        className={`h-2.5 w-2.5 ${
                          i <= Math.round(proof.googleReviews!.rating)
                            ? "fill-warning text-warning"
                            : "fill-muted text-muted"
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1 group-hover:text-primary transition-colors leading-tight">
                  {proof.googleReviews.totalRatings}{" "}
                  {proof.googleReviews.totalRatings === 1 ? "avaliação" : "avaliações"} Google ↗
                </p>
              </div>
            </a>
          )}

          {/* Years in business — only static "10+" can feel inflated if we
              don't have data to back it. Replaced with a qualitative "Autoridade"
              indicator that doesn't claim a specific number. */}
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Award className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-base md:text-lg font-extrabold text-foreground leading-none">
                Agente de Registro
              </p>
              <p className="text-[11px] text-muted-foreground mt-1 leading-tight">
                Parceira da SoluxID (AC ICP-Brasil)
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
